import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { Component, Element, h, Method, Prop, State, Watch } from "@stencil/core"
import { PaymentMethodEnum, PaymentMethodEnumsObj } from "../../enums/PaymentMethodEnum"
import { srcScreenError } from "../../helpers/CommonRenders"
import { resolvePrimaryActionState } from "../../helpers/PrimaryActionState"
import { getCardInputIframeUrl } from "../../helpers/Urls"
import { validatePaymentMethodEnum } from "../../helpers/Validators"
import { getLanguageHandler, getLocale, getSupportedLocaleData } from "../../languageHandler/LanguageHandler"
import { CardValidationModel } from "../../models/CardValidationModel"
import consentWarningIcon from "../commonCardInput/alert.svg"
import { CardHolderInfo } from "../commonCardInput/CardHolderInfo"
import { CardScreenStateModel } from "../commonCardInput/CardScreenStateModel"
import { eventResponseHandler, sendGetEncryptedCardEvent, sendValidateCardEvent } from "../commonCardInput/EventHandler"
import { ConsumerInfoFormConfig } from "../consumerInfoForm/ConsumerInfoFormConfig.model"
import { ConsumerInfoFormError, ConsumerInfoFormValidity } from "../consumerInfoForm/ConsumerInfoFormValidity.model"
import { ConsumerInfoValue, createEmptyConsumerInfoValue } from "../consumerInfoForm/ConsumerInfoValue.model"
import { InfoMessageModel } from "../infoMessage/InfoMessageModel"

type StoreCardElement = HTMLStoreCardElement & {
  reportConsumerValidity: () => Promise<boolean>
  focusFirstConsumerError: () => Promise<boolean>
  scrollToTop: () => Promise<boolean>
}

type AttemptState = "IDLE" | "AWAITING_VALIDATION" | "PREPARING_PAYMENT" | "AWAITING_ENCRYPTION" | "PROCESSING"
const TWO_STEP_PAYMENT_METHODS: PaymentMethodEnum[] = [
  PaymentMethodEnumsObj.MC,
  PaymentMethodEnumsObj.VISA,
  PaymentMethodEnumsObj.MC_VISA,
]

/** Shared validate-first and encrypt-later boundary for First-Time and Returning Add Card flows. */
@Component({
  tag: "two-step-card-flow",
  styleUrl: "TwoStepCardFlow.scss",
})
export class TwoStepCardFlow {
  @Element() hostElement: HTMLTwoStepCardFlowElement

  @Prop() locale?: string
  @Prop() paymentMethod: PaymentMethodEnum
  @Prop() env: "QA" | "PROD"
  @Prop() psp: string
  @Prop() cardHolderInfo?: CardHolderInfo
  @Prop() consumerInfoValue: ConsumerInfoValue = createEmptyConsumerInfoValue()
  @Prop() consumerInfoConfig?: ConsumerInfoFormConfig
  @Prop() consumerInfoErrors: ConsumerInfoFormError[] = []
  @Prop() showRememberMe: boolean = false
  @Prop() rememberMe: boolean = false
  @Prop() isLoading: boolean = false
  @Prop() infoMessage?: InfoMessageModel
  @Prop() consumerInfoOnChange?: (value: ConsumerInfoValue) => void
  @Prop() consumerInfoValidityOnChange?: (validity: ConsumerInfoFormValidity) => void
  @Prop() rememberMeOnChange?: (rememberMe: boolean) => void
  @Prop() cardValidationOnChange?: (validation: CardValidationModel) => boolean | Promise<boolean>
  @Prop() invalidCardOnChange?: (validation: CardValidationModel) => void
  @Prop() prepareStoreAndPay?: (cardBrand: "MASTERCARD" | "VISA") => boolean | Promise<boolean>
  /** Reports a recoverable asynchronous callback failure without exposing its contents. */
  @Prop() unexpectedErrorOnChange?: () => void
  /** Clears a recoverable action error when the user returns to the card step. */
  @Prop() goBackOnChange?: () => void
  @Prop() encryptedCardOnChange?: (
    cardState: CardScreenStateModel,
    rememberMe: boolean
  ) => void | Promise<void>

  @State() isCardInputLoaded = false
  @State() isConsentChecked = false
  @State() showConsentWarning = false
  @State() attemptState: AttemptState = "IDLE"
  @State() currentStep: "CARD" | "STORE_CARD" = "CARD"
  @State() selectedPaymentMethod?: PaymentMethodEnum
  @State() consumerErrorFocusRequest = 0

  private selectedCardValidation?: CardValidationModel
  private handledConsumerErrorFocusRequest = 0
  private componentUniqueId: string
  private cardInputIframeUrl: string
  private supportedLocale: string
  private supportedLang: string
  private languageHandler: LanguageHandler
  private consentCheckbox?: HTMLConsentCheckboxElement
  private cardScrollableContent?: HTMLDivElement
  private cardActions?: HTMLDivElement
  private storeCard?: StoreCardElement
  private pendingStepScrollReset = false
  private pendingConsentFeedbackReveal = false
  private errorMessage: string[] = []

  componentWillLoad() {
    if (!this.paymentMethod) {
      this.errorMessage.push("'paymentMethod' prop is not defined")
    } else if (
      !validatePaymentMethodEnum(this.paymentMethod) ||
      !TWO_STEP_PAYMENT_METHODS.includes(this.paymentMethod)
    ) {
      this.errorMessage.push("'paymentMethod' prop is not valid")
    }
    if (this.env !== "PROD" && this.env !== "QA") {
      this.errorMessage.push("'env' param must be 'QA' or 'PROD'")
    }
    if (typeof this.psp !== "string" || !this.psp.trim()) {
      this.errorMessage.push("'psp' param must be a non-empty string")
    }
    if (this.errorMessage.length > 0) {
      return
    }

    const localeData = getSupportedLocaleData(this.locale)
    this.supportedLocale = getLocale(localeData)
    this.supportedLang = localeData.lang
    this.languageHandler = getLanguageHandler(this.supportedLang)
    this.cardInputIframeUrl = getCardInputIframeUrl(this.env)
    this.componentUniqueId = Math.random().toString()
    window.addEventListener("message", this.onMessage)
  }

  disconnectedCallback() {
    window.removeEventListener("message", this.onMessage)
  }

  private finishAttempt() {
    this.attemptState = "IDLE"
  }

  private get isAttemptLocked() {
    return this.attemptState !== "IDLE" && this.attemptState !== "AWAITING_VALIDATION"
  }

  private handleUnexpectedAttemptError = () => {
    this.finishAttempt()
    this.unexpectedErrorOnChange?.()
  }

  componentDidRender() {
    if (this.pendingConsentFeedbackReveal) {
      this.pendingConsentFeedbackReveal = false
      this.revealCardActions()
      requestAnimationFrame(() => this.revealCardActions())
    }

    if (this.pendingStepScrollReset) {
      this.pendingStepScrollReset = false
      void this.scrollToTop()
      requestAnimationFrame(() => void this.scrollToTop())
    }

    if (this.consumerErrorFocusRequest !== this.handledConsumerErrorFocusRequest) {
      this.handledConsumerErrorFocusRequest = this.consumerErrorFocusRequest
      void this.storeCard?.focusFirstConsumerError()
    }
  }

  @Watch("consumerInfoErrors")
  consumerInfoErrorsChanged(errors: ConsumerInfoFormError[], previousErrors: ConsumerInfoFormError[] = []) {
    const hasNewError = errors.some(
      error =>
        !previousErrors.some(previous => previous.field === error.field && previous.code === error.code)
    )
    if (hasNewError) {
      this.consumerErrorFocusRequest += 1
    }
  }

  /** Resets the SDK overlay or the active embedded step to its initial position. */
  @Method()
  async scrollToTop(): Promise<boolean> {
    const paymentSheetOverlay = this.hostElement.closest(".payairPaymentSheetOverlay") as HTMLElement
    const containingScrollArea = this.hostElement.closest(".consumerCardScrollableContent") as HTMLElement
    const containingScreen = this.hostElement.closest(".cardScreen") as HTMLElement
    if (paymentSheetOverlay) {
      paymentSheetOverlay.scrollTop = 0
      paymentSheetOverlay.scrollLeft = 0
    }
    if (containingScrollArea) {
      containingScrollArea.scrollTop = 0
      containingScrollArea.scrollLeft = 0
    }
    if (containingScreen) {
      containingScreen.scrollTop = 0
      containingScreen.scrollLeft = 0
    }

    if (this.currentStep === "STORE_CARD") {
      return (await this.storeCard?.scrollToTop()) === true
    }
    if (!this.cardScrollableContent) return false

    this.cardScrollableContent.scrollTop = 0
    this.cardScrollableContent.scrollLeft = 0
    return true
  }

  private finishAttemptWithConsumerErrorFocus() {
    this.consumerErrorFocusRequest += 1
    this.finishAttempt()
  }

  private transitionToCardStep() {
    this.selectedCardValidation = undefined
    this.selectedPaymentMethod = undefined
    this.pendingStepScrollReset = true
    this.currentStep = "CARD"
    this.finishAttempt()
  }

  /** Returns the flow to the card-entry step after a recoverable card error. */
  @Method()
  async returnToCardStep(): Promise<void> {
    this.transitionToCardStep()
  }

  private revealCardActions() {
    if (!this.cardScrollableContent || !this.cardActions) return

    const scrollOwner =
      (this.hostElement.closest(".payairPaymentSheetOverlay") as HTMLElement) || this.cardScrollableContent
    const scrollableRect = scrollOwner.getBoundingClientRect()
    const actionsRect = this.cardActions.getBoundingClientRect()
    const offset = actionsRect.bottom - scrollableRect.bottom
    if (offset <= 0) return

    const top = (scrollOwner.scrollTop || 0) + offset
    if (typeof scrollOwner.scrollTo === "function") {
      scrollOwner.scrollTo({ behavior: "smooth", top })
    } else {
      scrollOwner.scrollTop = top
    }
  }

  private isSupportedCardBrand(validation: CardValidationModel): boolean {
    const paymentMethod =
      validation.cardBrand === "MASTERCARD"
        ? PaymentMethodEnumsObj.MC
        : validation.cardBrand === "VISA"
          ? PaymentMethodEnumsObj.VISA
          : undefined

    return Boolean(paymentMethod) && this.paymentMethod.split(",").includes(paymentMethod as string)
  }

  private areCardFieldsValid(validation: CardValidationModel): boolean {
    return (
      validation.isCardNumberValid &&
      validation.isPanExpirationDateValid &&
      validation.isCardSecurityCodeValid
    )
  }

  private toPaymentMethod(cardBrand: CardValidationModel["cardBrand"]): PaymentMethodEnum | undefined {
    if (cardBrand === "MASTERCARD") return PaymentMethodEnumsObj.MC
    if (cardBrand === "VISA") return PaymentMethodEnumsObj.VISA
    return undefined
  }

  private isValidEncryptedCard(cardState: CardScreenStateModel): boolean {
    return (
      this.areCardFieldsValid(cardState.encryptedCard) &&
      this.isSupportedCardBrand(cardState.encryptedCard) &&
      cardState.encryptedCard.cardBrand === this.selectedCardValidation?.cardBrand &&
      typeof cardState.encryptedCard.value === "string" &&
      cardState.encryptedCard.value.length > 0
    )
  }

  private onValidationResponse = async (validation: CardValidationModel) => {
    if (this.attemptState !== "AWAITING_VALIDATION") {
      return
    }
    this.attemptState = "PROCESSING"

    const areFieldsValid = this.areCardFieldsValid(validation)
    let isAccepted = areFieldsValid && this.isSupportedCardBrand(validation)
    if (isAccepted && this.cardValidationOnChange) {
      isAccepted = (await this.cardValidationOnChange(validation)) !== false
    }

    this.finishAttempt()
    if (!isAccepted) {
      if (areFieldsValid) {
        this.invalidCardOnChange?.(validation)
      }
      return
    }

    this.selectedCardValidation = validation
    this.selectedPaymentMethod = this.toPaymentMethod(validation.cardBrand)
    this.pendingStepScrollReset = true
    this.currentStep = "STORE_CARD"
  }

  private onEncryptedCardResponse = async (cardState: CardScreenStateModel) => {
    if (this.attemptState !== "AWAITING_ENCRYPTION") {
      return
    }
    this.attemptState = "PROCESSING"

    if (!this.isValidEncryptedCard(cardState)) {
      this.transitionToCardStep()
      this.invalidCardOnChange?.(cardState.encryptedCard)
      return
    }

    try {
      await this.encryptedCardOnChange?.(cardState, this.rememberMe)
      this.finishAttempt()
    } catch {
      this.handleUnexpectedAttemptError()
    }
  }

  onMessage = (event: MessageEvent) => {
    const response = eventResponseHandler(
      event,
      this.componentUniqueId,
      this.onEncryptedCardResponse,
      this.currentStep === "CARD" ? this.continueToStoreCard : undefined,
      this.onValidationResponse
    )
    if (response) {
      void response.catch(this.handleUnexpectedAttemptError)
    }
  }

  consentOnChange = (checked: boolean) => {
    this.isConsentChecked = checked
    if (checked) this.showConsentWarning = false
    if (!checked && this.attemptState === "AWAITING_VALIDATION") this.finishAttempt()
  }

  continueToStoreCard = async () => {
    if (
      this.isLoading ||
      this.isAttemptLocked ||
      this.currentStep !== "CARD"
    ) {
      return
    }
    this.showConsentWarning = !this.isConsentChecked
    if (!this.isConsentChecked) {
      this.pendingConsentFeedbackReveal = true
      await this.consentCheckbox?.focusInput()
      return
    }

    if (!this.isCardInputLoaded) {
      return
    }

    this.attemptState = "AWAITING_VALIDATION"
    sendValidateCardEvent(`${this.componentUniqueId}_cardInputFieldsIframe`)
  }

  storeAndPay = async () => {
    if (
      this.isLoading ||
      this.attemptState !== "IDLE" ||
      this.currentStep !== "STORE_CARD" ||
      !this.selectedCardValidation
    ) {
      return
    }
    this.attemptState = "PREPARING_PAYMENT"

    try {
      const isConsumerValid = (await this.storeCard?.reportConsumerValidity()) === true
      if (!isConsumerValid) {
        this.finishAttemptWithConsumerErrorFocus()
        return
      }

      const cardBrand = this.selectedCardValidation.cardBrand
      if (cardBrand !== "MASTERCARD" && cardBrand !== "VISA") {
        this.finishAttempt()
        return
      }

      if (this.prepareStoreAndPay && (await this.prepareStoreAndPay(cardBrand)) === false) {
        this.finishAttempt()
        return
      }

      this.attemptState = "AWAITING_ENCRYPTION"
      sendGetEncryptedCardEvent(`${this.componentUniqueId}_cardInputFieldsIframe`, this.cardHolderInfo)
    } catch {
      this.handleUnexpectedAttemptError()
    }
  }

  goBack = () => {
    if (this.attemptState !== "IDLE" || this.currentStep !== "STORE_CARD") {
      return
    }
    this.goBackOnChange?.()
    this.pendingStepScrollReset = true
    this.currentStep = "CARD"
  }

  render() {
    if (this.errorMessage.length > 0) {
      return srcScreenError("Two Step Card Flow", this.errorMessage)
    }

    const controlsDisabled = this.isLoading || this.isAttemptLocked
    const continueState = resolvePrimaryActionState({
      isLocallyValid: this.isConsentChecked,
      isBusy: controlsDisabled,
      isProcessing: this.isLoading,
    })

    return (
      <div class="twoStepCardFlow">
        <div class="twoStepCardFlowCardStep" hidden={this.currentStep !== "CARD"}>
          <div
            ref={element => (this.cardScrollableContent = element)}
            class="twoStepCardFlowScrollableContent">
            <slot name="card-step-header" />
            {this.infoMessage && Boolean(this.infoMessage.infoMessage) && (
              <info-message infoMessage={this.infoMessage} class="twoStepCardFlowMessage" />
            )}
            <iframe
              onLoad={() => (this.isCardInputLoaded = true)}
              allow="camera 'none'; microphone 'none'"
              class="twoStepCardFlowCardIframe"
              id={`${this.componentUniqueId}_cardInputFieldsIframe`}
              src={`${this.cardInputIframeUrl}?lang=${this.supportedLang}&env=${this.env}&psp=${this.psp}&id=${this.componentUniqueId}`}
            />
            <div ref={element => (this.cardActions = element)} class="twoStepCardFlowActions">
              <div class="twoStepCardFlowConsent">
                <consent-checkbox
                  ref={element => (this.consentCheckbox = element)}
                  locale={this.supportedLocale}
                  checked={this.isConsentChecked}
                  disabled={controlsDisabled}
                  error={this.showConsentWarning}
                  consentCheckboxOnChange={this.consentOnChange}
                />
              </div>
              {this.showConsentWarning && (
                <div class="twoStepCardFlowConsentWarning" role="alert">
                  <img
                    class="twoStepCardFlowConsentWarningIcon"
                    src={consentWarningIcon}
                    alt=""
                    aria-hidden="true"
                  />
                  <span>{this.languageHandler.getString("uncheckedConstentWarning")}</span>
                </div>
              )}
              <button
                class={{
                  twoStepCardFlowContinue: true,
                  primaryActionSoftDisabled: continueState.softDisabled,
                }}
                type="button"
                disabled={continueState.disabled}
                onClick={this.continueToStoreCard}>
                {continueState.showSpinner ? (
                  <loading-spinner loadingText={this.languageHandler.getString("processing")} />
                ) : (
                  this.languageHandler.getString("continue")
                )}
              </button>
              <div class="twoStepCardFlowSecondaryAction">
                <slot name="card-step-footer" />
              </div>
            </div>
          </div>
        </div>

        <div class="twoStepCardFlowStoreStep" hidden={this.currentStep !== "STORE_CARD"}>
          {this.selectedPaymentMethod && (
            <store-card
              ref={element => (this.storeCard = element as StoreCardElement)}
              locale={this.supportedLocale}
              paymentMethod={this.selectedPaymentMethod}
              consumerInfoValue={this.consumerInfoValue}
              consumerInfoConfig={this.consumerInfoConfig}
              consumerInfoErrors={this.consumerInfoErrors}
              showValidationErrors={this.consumerInfoErrors.length > 0}
              showRememberMe={this.showRememberMe}
              rememberMe={this.rememberMe}
              disabled={controlsDisabled}
              isLoading={this.isLoading}
              infoMessage={this.infoMessage}
              consumerInfoOnChange={this.consumerInfoOnChange}
              consumerInfoValidityOnChange={this.consumerInfoValidityOnChange}
              rememberMeOnChange={this.rememberMeOnChange}
              storeAndPayOnClick={this.storeAndPay}
              goBackOnClick={this.goBack}
            />
          )}
        </div>
      </div>
    )
  }
}
